import React, { useState } from 'react';
import { X, Eye, EyeOff, Lock } from 'lucide-react';
import { resetPassword } from '../api/index';
import { toast } from 'react-toastify';

const ChangePasswordModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.new_password || !formData.confirm_password) {
      toast.error('Please fill in all fields');
      return;
    }

    if (formData.new_password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.new_password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    
    try {
      // Get email from session storage
      const userDetails = JSON.parse(localStorage.getItem('user_details'));
      const email = userDetails?.email;

      if (!email) {
        toast.error('User email not found. Please login again.');
        return;
      }

      const response = await resetPassword({
        email,
        new_password: formData.new_password,
        confirm_password: formData.confirm_password
      });

      if (response.data.status) {
        toast.success(response.data.message || 'Password changed successfully');
        setFormData({ new_password: '', confirm_password: '' });
        onClose();
      } else {
        toast.error(response.data.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('Password change error:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('An error occurred while changing password');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({ new_password: '', confirm_password: '' });
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/70 z-50">
      <div className="bg-[#1A1A1A] rounded-xl shadow-lg w-[400px] p-6 border border-[#F9AC1D] bg-gradient-to-b from-[#2b220fdb] to-[#18140bec]">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F3B7531A]">
              <Lock className="text-[#F3B753] w-5 h-5" />
            </div>
            <h2 className="text-lg font-semibold text-white">Change Password</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-white transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* New Password */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="new_password"
                value={formData.new_password}
                onChange={handleInputChange}
                disabled={loading}
                className="w-full px-4 py-3 bg-[#2A2A2A] border border-[#454545] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-[#F3B753] focus:ring-1 focus:ring-[#F3B753] disabled:opacity-50"
                placeholder="Enter new password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors disabled:opacity-50"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Confirm Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirm_password"
                value={formData.confirm_password}
                onChange={handleInputChange}
                disabled={loading}
                className="w-full px-4 py-3 bg-[#2A2A2A] border border-[#454545] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-[#F3B753] focus:ring-1 focus:ring-[#F3B753] disabled:opacity-50"
                placeholder="Confirm new password"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={loading}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors disabled:opacity-50"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="flex-1 border border-[#F3B753] text-[#F3B753] py-3 rounded-lg font-medium text-sm hover:bg-[#F3B753] hover:text-black transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-[#F3B753] text-black py-3 rounded-lg font-medium text-sm hover:bg-[#e6a800] transition disabled:opacity-50 flex items-center justify-center"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
              ) : (
                'Change Password'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordModal;


